import type { PipelineColumn } from '../types/types';
import { leadPriorityOrFilters } from './lead-priority';

// Server-side filters pushed into the contacts query (CRMContext.fetchContacts).
// Filtering the in-memory `contacts` array in KanbanPage only ever saw the
// loaded 500-row page, so on big agencies (AcBienes 1.8k, Olga 12k, CityMax
// 10k) every match past it was unreachable — search first, then an asesor's
// clients via the Filtros drawer (2026-09-14).
export type ContactListServerFilters = {
  // Partial across name/phone/email/tags — resolved to one OR string by
  // CRMContext.resolveContactSearchOr (needs a contact_tags lookup).
  search?: string;
  // Exact-match tag via the contact_tags!inner embed (see tagEmbed).
  tag?: string;
  agent_id?: string;
  source?: string;        // partial, case-insensitive on referred_by
  created_from?: string;  // YYYY-MM-DD, UTC-day bounds like the old in-memory filter
  created_to?: string;
  updated_from?: string;
  updated_to?: string;
  priority?: string;
  // Prospectos badges are computed (computeLeadPriority → leadPriorityOrFilters);
  // Propietarios filters the stored `priority` column.
  priority_computed?: boolean;
};

// Every query over the contacts list — the page, its badge enrichment, the
// header totals and the kanban priority chips — narrows through here, so they
// can never disagree about which rows match. A tag narrow needs tagEmbed() in
// the caller's select.
export function narrowContacts(q: any, f: ContactListServerFilters, searchOr: string | null, intakeColumnId: string | undefined): any {
  if (f.tag) q = q.eq('filter_tags.tag', f.tag);
  if (searchOr) q = q.or(searchOr);
  if (f.agent_id) q = q.eq('agent_id', f.agent_id);
  // Escape LIKE wildcards so the text matches literally, as includes() did.
  if (f.source) q = q.ilike('referred_by', `%${f.source.replace(/[\\%_]/g, '\\$&')}%`);
  if (f.created_from) q = q.gte('created_at', f.created_from);
  if (f.created_to) q = q.lte('created_at', `${f.created_to}T23:59:59.999Z`);
  if (f.updated_from) q = q.gte('updated_at', f.updated_from);
  if (f.updated_to) q = q.lte('updated_at', `${f.updated_to}T23:59:59.999Z`);
  if (f.priority) {
    if (f.priority_computed) {
      for (const group of leadPriorityOrFilters(f.priority, intakeColumnId)) q = q.or(group);
    } else {
      q = q.eq('priority', f.priority);
    }
  }
  return q;
}

// The Filtros drawer lives in the kanban's URL (?f_*). Its bottom zones
// (Archivados / Base de datos / Negociación) get the same query string, so
// opening one — and coming back — keeps the active Filtros.
export const FILTER_PARAM_KEYS = [
  'f_priority', 'f_agent', 'f_source', 'f_tag',
  'f_created_from', 'f_created_to', 'f_updated_from', 'f_updated_to',
] as const;

/** Only the Filtros params, in a stable order ('' when none are set). */
export function filterQueryString(params: URLSearchParams): string {
  const out = new URLSearchParams();
  for (const k of FILTER_PARAM_KEYS) {
    const v = params.get(k);
    if (v) out.set(k, v);
  }
  return out.toString();
}

/** How many Filtros groups are set (date ranges count once), for the badge. */
export function activeFilterCount(params: URLSearchParams): number {
  const g = (k: string) => params.get(k) || '';
  return [
    g('f_priority'), g('f_agent'), g('f_source'), g('f_tag'),
    g('f_created_from') || g('f_created_to'),
    g('f_updated_from') || g('f_updated_to'),
  ].filter(Boolean).length;
}

export function filtersFromQuery(qs: string, priorityComputed: boolean): ContactListServerFilters {
  const p = new URLSearchParams(qs);
  const get = (k: (typeof FILTER_PARAM_KEYS)[number]) => p.get(k) || undefined;
  return {
    tag: get('f_tag'),
    agent_id: get('f_agent'),
    source: get('f_source'),
    created_from: get('f_created_from'),
    created_to: get('f_created_to'),
    updated_from: get('f_updated_from'),
    updated_to: get('f_updated_to'),
    priority: get('f_priority'),
    priority_computed: priorityComputed,
  };
}

export const tagEmbed = (f: ContactListServerFilters) => (f.tag ? ', filter_tags:contact_tags!inner(tag)' : '');

// Same intake-column pick as applyLeadPriorities (lowest sort_order).
export const intakeColumnIdOf = (cols: PipelineColumn[]) =>
  cols.length > 0 ? String(cols.reduce((a, b) => (a.sort_order < b.sort_order ? a : b)).id) : undefined;
